import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Raccoglie il clic sulla mappa e disegna un cerchio nel punto scelto (niente icone-immagine -> nessun 404).
function Picker({ punto, onPick }) {
  useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
  return punto ? (
    <CircleMarker center={[punto.lat, punto.lon]} radius={10}
      pathOptions={{ color: '#f97316', weight: 3, fillColor: '#f97316', fillOpacity: 0.4 }} />
  ) : null;
}

/* Segue l'azienda sotto il mouse: la centra e avvicina di un paio di livelli, cosi' si
   vede in che via sta e non solo un puntino in mezzo agli altri. Quando il mouse esce
   dall'elenco la mappa TORNA come l'aveva lasciata chi la stava guardando: la vista di
   partenza si memorizza al primo passaggio, non a ogni riga. */
function SeguiEvidenziata({ azienda, avvicina = 2, zoomMax = 17 }) {
  const map = useMap();
  const partenza = useRef(null);
  useEffect(() => {
    if (azienda && azienda.lat != null && azienda.lon != null) {
      if (!partenza.current) partenza.current = { centro: map.getCenter(), zoom: map.getZoom() };
      const z = Math.min(zoomMax, Math.max(map.getZoom(), partenza.current.zoom + avvicina));
      map.flyTo([azienda.lat, azienda.lon], z, { duration: 0.45 });
    } else if (partenza.current) {
      const { centro, zoom } = partenza.current;
      partenza.current = null;
      map.flyTo(centro, zoom, { duration: 0.45 });
    }
  }, [azienda, map, avvicina, zoomMax]);
  return null;
}

/* Con l'altezza a percentuale il riquadro cambia misura (finestra ridimensionata, console
   che si apre): Leaflet non se ne accorge da solo e disegna le tile storte o nere. */
function AdattaAlRiquadro() {
  const map = useMap();
  useEffect(() => {
    const fix = () => { try { map.invalidateSize(); } catch (e) { /* no-op */ } };
    const ts = [0, 150, 400].map((d) => setTimeout(fix, d));
    const el = map.getContainer();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(fix); ro.observe(el); }
    window.addEventListener('resize', fix);
    return () => { ts.forEach(clearTimeout); window.removeEventListener('resize', fix); if (ro) ro.disconnect(); };
  }, [map]);
  return null;
}

/* Mappa interattiva: si clicca il punto di riferimento (onPick) e si vedono le aziende
   trovate. `evidenziata` e' quella sotto il mouse nell'elenco: si ingrandisce e mostra il
   nome, cosi' si capisce DOVE sta il cliente senza cercarlo.
   Tutto con OpenStreetMap: nessuna chiave, nessun costo. */
export default function ZonaMap({ punto, onPick, height = 300, aziende, evidenziata, selezionate,
                                  segui, onEvidenzia }) {
  const lista = (aziende || []).filter((a) => a && a.lat != null && a.lon != null);
  const idEvid = evidenziata && evidenziata.id;
  const sel = selezionate || new Set();
  return (
    <MapContainer center={punto ? [punto.lat, punto.lon] : [42, 12.5]} zoom={punto ? 15 : 5}
      style={{ height, width: '100%', borderRadius: 12 }} scrollWheelZoom>
      <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <Picker punto={punto} onPick={onPick} />
      {lista.map((a) => {
        const on = a.id === idEvid;
        const scelta = sel.has && sel.has(a.id);     // spuntata per l'import
        const tinta = scelta ? '#22c55e' : '#d98a1a';
        return (
          <CircleMarker key={a.id} center={[a.lat, a.lon]} radius={on ? 15 : scelta ? 11 : 9}
            eventHandlers={onEvidenzia ? {
              mouseover: () => onEvidenzia(a),
              mouseout: () => onEvidenzia(null),
              click: () => onEvidenzia(a),        // sul tocco il passaggio del mouse non esiste
            } : undefined}
            pathOptions={on
              ? { color: '#ffffff', weight: 4, fillColor: tinta, fillOpacity: 1 }
              : { color: '#ffffff', weight: 2, fillColor: tinta, fillOpacity: 0.95 }}>
            <Tooltip permanent={on} direction="top" offset={[0, -8]}>{a.nome || '—'}</Tooltip>
          </CircleMarker>
        );
      })}
      {/* Il volo segue SOLO l'azienda scelta dall'elenco: se seguisse anche quella toccata
          sulla mappa, la mappa scapperebbe sotto il puntatore mentre la si guarda. */}
      <SeguiEvidenziata azienda={segui} />
      <AdattaAlRiquadro />
    </MapContainer>
  );
}
